import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-dismiss for a batch surface's PURE-SUCCESS state (GDN-3236): "N
 * claims sent — every selected claim reached the clearinghouse" earns its
 * exit after a quiet interval; every other terminal outcome is actionable
 * and persists until the biller dismisses it. That asymmetry is the point.
 *
 * Decided semantics (researched with Alfred):
 * - NO countdown UI — a timer visual adds motion and urgency to a message
 *   that requires no action; countdowns are for expiry that forecloses an
 *   option (undo windows), which this does not.
 * - The timer runs only while the tab is VISIBLE: a completion landing
 *   while the biller is elsewhere must not expire unseen. Hidden pauses;
 *   visible resumes with the remaining time.
 * - Hover and keyboard focus PAUSE (remaining-time, not restart); leaving
 *   resumes. Spread `holdProps` on the surface's root.
 * - When the timer elapses, `dismissing` flips true so the surface can run
 *   its CSS exit transition; `onDismiss` fires after `collapseMs`. Under
 *   prefers-reduced-motion the exit is instant (house rule: CSS handles
 *   motion; JS only schedules removal).
 *
 * Not an analytics event anywhere — auto-dismiss is not a user interaction.
 */

export const BATCH_SUCCESS_AUTO_DISMISS_MS = 8000;
export const BATCH_SUCCESS_COLLAPSE_MS = 220;

export interface BatchSuccessAutoDismiss {
  /** True while the CSS exit transition should be applied. */
  dismissing: boolean;
  /** Spread on the surface root: pauses the timer on hover/focus. */
  holdProps: {
    onBlur: () => void;
    onFocus: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

export function useBatchSuccessAutoDismiss(
  active: boolean,
  onDismiss: () => void,
): BatchSuccessAutoDismiss {
  const [dismissing, setDismissing] = useState(false);
  const remainingRef = useRef(BATCH_SUCCESS_AUTO_DISMISS_MS);
  const armedAtRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* A COUNT, not a boolean: hover and keyboard focus can overlap, and
     ending one must not resume the timer while the other still holds. */
  const holdsRef = useRef(0);
  const dismissingRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (armedAtRef.current !== null) {
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - armedAtRef.current));
      armedAtRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    if (timerRef.current !== null) return;
    if (holdsRef.current > 0 || document.visibilityState !== 'visible') return;
    armedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      armedAtRef.current = null;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      if (reduceMotion) {
        onDismissRef.current();
        return;
      }
      dismissingRef.current = true;
      setDismissing(true);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onDismissRef.current();
      }, BATCH_SUCCESS_COLLAPSE_MS);
    }, remainingRef.current);
  }, []);

  useEffect(() => {
    if (!active) {
      clearTimer();
      dismissingRef.current = false;
      /* holdsRef deliberately survives this reset: holds track the POINTER
         and FOCUS, which don't change when the batch state does. A biller
         hovering the bar while the batch finishes (active flips false→true
         under a stationary cursor) must stay held — zeroing here made the
         success expire under their pointer. */
      setDismissing(false);
      remainingRef.current = BATCH_SUCCESS_AUTO_DISMISS_MS;
      return;
    }
    arm();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') arm();
      else clearTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimer();
    };
  }, [active, arm, clearTimer]);

  /* Once the exit transition has begun, the decision is made — a hover
     mid-collapse must not resurrect the bar half-faded. */
  const hold = useCallback(() => {
    if (dismissingRef.current) return;
    holdsRef.current += 1;
    clearTimer();
  }, [clearTimer]);
  const release = useCallback(() => {
    if (dismissingRef.current) return;
    holdsRef.current = Math.max(0, holdsRef.current - 1);
    if (holdsRef.current === 0) arm();
  }, [arm]);

  return {
    dismissing,
    holdProps: { onBlur: release, onFocus: hold, onMouseEnter: hold, onMouseLeave: release },
  };
}
